import { getCookie } from '../helpers/cookies';

/**
 * Language mapping type
 */
type LanguageCode = 'EN' | 'ES' | 'FR' | 'PT' | 'DE' | 'IT' | '';

/**
 * Partner configuration interface
 */
export interface PartnerConfig {
  partner_id: string;
  partner_name: string;
  country_code: string;
}

/**
 * Global constants interface
 */
export interface GlobalConstants extends PartnerConfig {
  language: LanguageCode;
  instance_env: 'local' | 'uat' | 'prod';
}

/**
 * UTM parameters interface
 */
interface UTMParams {
  utm_source: string | null;
  utm_campaign: string | null;
  utm_medium: string | null;
  utm_content: string | null;
}

/**
 * Get language from idiom cookie
 */
function getLanguageFromIdiom(): LanguageCode {
  const idiom = getCookie('idiom');

  const languageMap: Record<string, LanguageCode> = {
    en_US: 'EN',
    es_ES: 'ES',
    fr_FR: 'FR',
    pt_BR: 'PT',
    'de-DE': 'DE',
    it_IT: 'IT',
  };

  return (idiom && languageMap[idiom]) || '';
}

/**
 * Parse UTM parameters from URL
 */
function getUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return {
      utm_source: null,
      utm_campaign: null,
      utm_medium: null,
      utm_content: null,
    };
  }

  const urlParams = new URLSearchParams(window.location.search);

  return {
    utm_source: urlParams.get('utm_source'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_medium: urlParams.get('utm_medium'),
    utm_content: urlParams.get('utm_content'),
  };
}

/**
 * Parse partner info from UTM content
 */
function parseUTMContent(
  utmContent: string | null
): { partner_name: string; country_code: string } | null {
  if (!utmContent) return null;

  const parts = utmContent.split('-');
  if (parts.length >= 2) {
    return {
      partner_name: parts[0],
      country_code: parts[1],
    };
  }

  return null;
}

/**
 * Check if UTM source is from specific channels
 */
function isChannelUTMSource(utmSource: string | null): boolean {
  if (!utmSource) return false;

  const channelSources = ['sms', 'IVR', 'web', 'certificate', 'email'];
  return channelSources.includes(utmSource);
}

/**
 * Get partner configuration based on domain and UTM parameters
 */
function getPartnerConfig(): PartnerConfig {
  if (typeof window === 'undefined') {
    return {
      partner_id: '',
      partner_name: '',
      country_code: '',
    };
  }

  const hostname = window.location.hostname;
  const { utm_source, utm_campaign, utm_medium, utm_content } = getUTMParams();

  // Generic domain
  if (hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
    if (utm_source && utm_campaign && utm_medium && utm_content) {
      return {
        partner_id: utm_campaign,
        partner_name: utm_medium,
        country_code: utm_content,
      };
    } else if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'undefined EU',
        partner_name: parsed?.partner_name || 'undefined EU',
        country_code: parsed?.country_code || 'undefined EU',
      };
    } else {
      return {
        partner_id: 'undefined EU',
        partner_name: 'undefined EU',
        country_code: 'undefined EU',
      };
    }
  }

  // Trip UK domain
  if (hostname === import.meta.env.VITE_DOMAIN_TRIPUK) {
    if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'TRIP.COM1',
        partner_name: parsed?.partner_name || 'Trip.com',
        country_code: parsed?.country_code || 'GB',
      };
    } else {
      return {
        partner_id: 'TRIP.COM1',
        partner_name: 'Trip.com',
        country_code: 'GB',
      };
    }
  }

  // US Chubb domain
  if (hostname === import.meta.env.VITE_DOMAIN_US_CHUBB) {
    if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'CHUBB_EXT',
        partner_name: parsed?.partner_name || 'Chubb',
        country_code: parsed?.country_code || 'US',
      };
    } else {
      return {
        partner_id: 'CHUBB_EXT',
        partner_name: 'Chubb',
        country_code: 'US',
      };
    }
  }

  // US MetLife domain
  if (hostname === import.meta.env.VITE_DOMAIN_US_METLIFE) {
    if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'METLIFE1',
        partner_name: parsed?.partner_name || 'Metlife',
        country_code: parsed?.country_code || 'US',
      };
    } else {
      return {
        partner_id: 'METLIFE1',
        partner_name: 'Metlife',
        country_code: 'US',
      };
    }
  }

  // USTI domain
  if (hostname === import.meta.env.VITE_DOMAIN_USTI) {
    if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'USTI1',
        partner_name: parsed?.partner_name || 'Usti',
        country_code: parsed?.country_code || 'EU',
      };
    } else {
      return {
        partner_id: 'USTI1',
        partner_name: 'Usti',
        country_code: 'EU',
      };
    }
  }

  // US domain
  if (hostname === import.meta.env.VITE_DOMAIN_US) {
    if (utm_source && utm_campaign && utm_medium && utm_content) {
      return {
        partner_id: utm_campaign,
        partner_name: utm_medium,
        country_code: utm_content,
      };
    } else if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'undefined US',
        partner_name: parsed?.partner_name || 'undefined US',
        country_code: parsed?.country_code || 'undefined US',
      };
    } else {
      return {
        partner_id: 'undefined US',
        partner_name: 'undefined US',
        country_code: 'undefined US',
      };
    }
  }

  // WWIIS domain
  if (hostname === import.meta.env.VITE_DOMAIN_WWIIS) {
    if (isChannelUTMSource(utm_source)) {
      const parsed = parseUTMContent(utm_content);
      return {
        partner_id: 'WWIIS',
        partner_name: parsed?.partner_name || 'WWIIS',
        country_code: parsed?.country_code || 'GB',
      };
    } else {
      return {
        partner_id: 'WWIIS',
        partner_name: 'WWIIS',
        country_code: 'GB',
      };
    }
  }

  // Default
  return {
    partner_id: '',
    partner_name: '',
    country_code: '',
  };
}

/**
 * Get instance environment based on hostname
 */
function getInstanceEnvironment(): 'local' | 'uat' | 'prod' {
  if (typeof window === 'undefined') {
    return 'local';
  }

  const hostname = window.location.hostname;
  const href = window.location.href;

  if (hostname.includes('test')) {
    return 'uat';
  } else if (hostname.includes('localhost')) {
    return 'local';
  } else if (hostname.includes('uk-trip-costumer')) {
    return 'local';
  } else if (href.includes(':3000')) {
    return 'local';
  } else if (href.includes('azurewebsites')) {
    return 'uat';
  } else if (hostname.includes('ppa')) {
    return 'uat';
  } else {
    return 'prod';
  }
}

/**
 * Get all global constants
 * This function should be called within React components or hooks
 */
export function getGlobalConstants(): GlobalConstants {
  const language = getLanguageFromIdiom();
  const partnerConfig = getPartnerConfig();
  const instance_env = getInstanceEnvironment();

  return {
    language,
    ...partnerConfig,
    instance_env,
  };
}

/**
 * Individual getter functions for use in components
 */
export function getLanguage(): LanguageCode {
  return getLanguageFromIdiom();
}

export function getPartnerId(): string {
  return getPartnerConfig().partner_id;
}

export function getPartnerName(): string {
  return getPartnerConfig().partner_name;
}

export function getCountryCode(): string {
  return getPartnerConfig().country_code;
}

export function getInstanceEnv(): 'local' | 'uat' | 'prod' {
  return getInstanceEnvironment();
}

// Default export for convenience
export default getGlobalConstants;
